import { Aircraft, AircraftFullWbProfileInfo } from "./models/aircraft";
import { Organization } from "./models/organization";
import { Passenger } from "./models/passenger";
import axios from "axios";
import { auth } from "./firebase";
import { onAuthStateChanged, getIdToken } from 'firebase/auth';
import {store} from "./store/store"
import { Crew } from "./models/crew";
import { ImportedRoute, Route, RoutePoint } from "./models/route";
import Flight, { FlightBriefing, FlightPostFlightData, FlightWb, FlightWbRevisionChanges } from "./models/flight";
import { AutorouterStatus } from "./pages/auth/AutorouterAuthPage";
import { UserData, UserInvite } from "./models/userInvite";
import FlightPlan from "./models/flightPlan";
import ToolsFlight from "./models/toolsFlight";
import { Airfield, AirfieldSlotContactDetails, AirfieldSlotTime, AirfieldSlotType } from "./models/airfieldSlots";
import moment from "moment";


if (process.env.NODE_ENV === "development") {
    // axios.defaults.baseURL = "http://172.20.10.2:5005"
    axios.defaults.baseURL = "http://localhost:5006"
} else {
    axios.defaults.baseURL = process.env.REACT_APP_BRIEF_API_URL
}

const briefApi = axios.create({baseURL: axios.defaults.baseURL + "/brief"})

briefApi.interceptors.request.use( async (config) => {
    
    //For Anonymous RequestInfo requests that use JWT
    if (config.headers.Authorization){
        return config
    }

    //For Shortlinks fetch that need no auth
    if (config.url?.startsWith("/shortlinks/")){
        return config
    }

    if (config.url?.startsWith("/tools/")){
        return config
    }

    if (auth.currentUser){
        config.headers["Authorization"] = "Pilot " + (await auth.currentUser.getIdToken())
    }else{
        try{
            const idToken = await getUserIdToken()
            config.headers["Authorization"] = "Pilot " + idToken
        }catch(e: any){
            if (e === "User not logged in"){
                if (window.location.pathname.startsWith("/invites/") ) {
                    window.location.href = "/auth/register?redirectTo=" + window.location.pathname
                }else{
                    window.location.href = "/auth/login" + ((window.location.pathname !== "/") ? "?redirectTo=" + window.location.pathname : "")
                }
            }else{
                throw e 
            }            
        }
    }
    
    return config
})

briefApi.interceptors.request.use( async (config) => {
    if (config.url?.includes(":orgId")){
        const orgId  = store.getState().organizations.selectedOrganizationId!
        config.url = config.url!.replace(":orgId", orgId)
    }
    
    return config
})

//Reject with the most specific message the server gave, AS A STRING. The string is the contract callers
//rely on: AddAircraftPage compares the rejection to "NOT_FOUND" and renders it directly as a React child,
//so handing back an Error both breaks that branch and crashes the render.
//Three body shapes have to be handled or the reason is lost on the way to the UI:
//  - `{ error: "..." }`  — most endpoints
//  - a bare string        — a dozen still send one ("Flight plan not found", "Unknown error")
//  - no response at all   — a timeout, a dropped connection, or a function that crashed
//That last case is why every access below is optional-chained: reading `error.response.data` when there
//is no response threw "Cannot read properties of undefined (reading 'data')" from inside this
//interceptor, which then surfaced INSTEAD of the real failure.
function messageFromErrorBody(data: any): string | undefined {
    if (typeof data === "string" && data.trim().length > 0) return data
    if (data && typeof data.error === "string" && data.error.length > 0) return data.error
    return undefined
}

briefApi.interceptors.response.use((response) => {
    return response
}, async (error) => {
    if (error.response?.status === 403 || error.response?.status === 401){
        await auth.signOut()
    }

    const reason = (message?: string) => Promise.reject(message || error?.message || "Request failed. Please try again.")

    if (error.response?.data instanceof Blob){ //When ResponseType is Blob
        try {
            const jsonText: any = await (error.response.data as Blob).text()
            return reason(messageFromErrorBody(JSON.parse(jsonText)))
        } catch {
            //A non-JSON blob (an HTML error page, a truncated download) — fall back to the axios message.
            return reason()
        }
    }
    return reason(messageFromErrorBody(error.response?.data))
})

async function getUserIdToken(): Promise<string>{
    return new Promise((resolve, reject) => {
        const unsub = onAuthStateChanged(auth, async (user) => {
            if (user) {
                const token = await getIdToken(user);
                resolve(token)
            } else {
                reject("User not logged in")
            }
            unsub();
        });
    })
}

export type IfrOptimize = "time" | "fuel" | "preferred"

export type IfrRouteDelay = {
    locationIdent?: string
    regulations?: string[]
    airspace?: string
    code?: string
    startTime?: number //epoch seconds
    endTime?: number //epoch seconds
    geometry?: any //GeoJSON MultiPolygon
}

export type IfrRouteWaypoint = {
    name: string
    ident?: string
    lat?: number
    lon?: number
    type?: string
    altitude?: number
    dist?: number
    airway?: string
}

export type IfrRouteFound = {
    status: "found"
    routeId: string
    optimize: string
    level?: number //requested flight level this option was computed for
    field15: string
    fullFpl?: string
    routeDist?: number //NM
    routeFuel?: number
    routeTime?: number //seconds
    initialAltitude?: number
    validation: string //raw IFPUV string ("NO ERRORS" = ok)
    delays: IfrRouteDelay[]
    waypoints: IfrRouteWaypoint[]
}

export type IfrRouteResult =
    | IfrRouteFound
    | { status: "pending"; routeId: string }
    | { status: "failed"; routeId: string; error: string }

//Planning-input overrides from the route-planner UI (used for THIS planning run only; they do not
//change the saved flight/plan — edit the flight form to persist them).
export type IfrPlanningOverrides = {
    departure?: string
    destination?: string
    alternate1?: string
    alternate2?: string
    departureDateTime?: string | Date
    level?: number //requested flight level (e.g. 100 for FL100)
    distanceNM?: number //autorouter route distance from the chosen route — stored to drive the on-the-fly EET
}

//Stateless route-find input: planning happens from these values alone, no saved entity needed.
export type IfrRouteFindInput = {
    departure: string
    destination: string
    departureDateTime: Date | string
    alternate1?: string
    alternate2?: string
    level?: number //optional for Z (VFR first) — the IFR level lives inside the route string
    flightRules?: "I" | "Y" | "Z" //route checks honor these; route-find is always pure IFR
    optimizes?: string[]
    levels?: number[] //explore these flight levels (each optimize × level combination, capped server-side)
    aircraft: {
        registration: string
        icaoType: string
        equipmentCodes: string
        surveillanceEquipmentCodes: string
        cruiseSpeedKt: number
        fuelUnit?: string
        fuelPerHour: number
        maxMass: number
    }
}

export type ValidateFplResponse = {
    validation: { result: "ok" | "errors"; validation: string; errors: string[] }
    fplCode: string
}

export module AviTracerApi {

    export async function getOrganizations(): Promise<Organization[]> {
        return (await briefApi.get("/organizations")).data
    }

    export async function getPassengers(): Promise<Passenger[]> {
        return (await briefApi.get("/organizations/:orgId/passengers")).data
    }

    export async function getPassengerWithId(passengerId: string, orgId?: string): Promise<Passenger> {
        return (await briefApi.get("/organizations/"+(orgId ?? ":orgId")+"/passengers/"+passengerId)).data
    }

    export async function savePassenger(passenger: Passenger): Promise<Passenger> {
        if (passenger.id){
            return (await briefApi.put("/organizations/:orgId/passengers/"+passenger.id, passenger)).data
        }else{
            return (await briefApi.post("/organizations/:orgId/passengers", passenger)).data
        }
    }

    export async function archivePassenger(passengerId: Passenger["id"]): Promise<void> {
        return (await briefApi.delete("/organizations/:orgId/passengers/"+passengerId+"/archive")).data
    }

    export async function passengerRequestInfoGenerateUrl(passengerId: string): Promise<{url: string}>{
        return (await briefApi.post("/organizations/:orgId/passengers/"+passengerId+"/requestInfoShortlinkGenerate")).data
    }

    export async function passengerRequestInfoGetData(token: string): Promise<{passenger: Passenger}>{
        return (await briefApi.get("/requestInfo/passenger", {headers:{"Authorization": "Bearer "+ token}})).data
    }

    export async function passengerRequestInfoSubmit(passenger: Passenger, signatureBase64: string, token: string): Promise<void>{
        return (await briefApi.post("/requestInfo/passenger",{signatureBase64, passenger}, {headers:{"Authorization": "Bearer "+ token}})).data
    }

    export async function getCrew(): Promise<Crew[]> {
        return (await briefApi.get("/organizations/:orgId/crew")).data
    }

    export async function getCrewWithId(crewId: string, orgId?: string): Promise<Crew> {
        return (await briefApi.get("/organizations/"+(orgId ?? ":orgId")+"/crew/"+crewId)).data
    }

    export async function addCrew(crew: Crew): Promise<Crew>{
        return (await briefApi.post("/organizations/:orgId/crew", crew)).data
    }
    
    export async function editCrew(crew: Crew): Promise<Crew>{
        return (await briefApi.put("/organizations/:orgId/crew/"+crew.id, crew)).data
    }

    export async function archiveCrew(crewId: Crew["id"]): Promise<void> {
        return (await briefApi.delete("/organizations/:orgId/crew/"+crewId+"/archive")).data
    }

    export async function crewRequestInfoGenerateUrl(crewId: string): Promise<{url: string}>{
        return (await briefApi.post("/organizations/:orgId/crew/"+crewId+"/requestInfoShortlinkGenerate")).data
    }

    export async function crewRequestInfoGetData(token: string): Promise<{crew: Crew}>{
        return (await briefApi.get("/requestInfo/crew", {headers:{"Authorization": "Bearer "+ token}})).data
    }

    export async function crewRequestInfoSubmit(signatureBase64: string, token: string): Promise<void>{
        return (await briefApi.post("/requestInfo/crew",{signatureBase64}, {headers:{"Authorization": "Bearer "+ token}})).data
    }

    export async function inviteCrew(crew: Crew): Promise<Crew> {
        return (await briefApi.post("/organizations/:orgId/crew/"+crew.id+"/invite")).data
    }

    export async function getUserInvite(inviteId: string): Promise<UserInvite> {
        return (await briefApi.get("/user/invites/"+inviteId)).data
    }

    export async function acceptUserInvite(inviteId: string, userData: UserData, allowOrganizationToEdit: boolean): Promise<Organization> {
        return (await briefApi.post("/user/invites/"+inviteId+"/accept", {allowOrganizationToEdit, ...userData})).data
    }

    export async function getAircraft(): Promise<Aircraft[]> {
        return (await briefApi.get("/organizations/:orgId/aircraft")).data
    }

    export async function createAircraft(registration: string): Promise<Aircraft> {
        return (await briefApi.post("/organizations/:orgId/aircraft", {registration})).data
    }

    export async function createAircraftFromShared(registration: string): Promise<Aircraft> {
        return (await briefApi.post("/organizations/:orgId/aircraft/shared", {registration})).data
    }

    export async function updateAircraft(aircraft: Aircraft): Promise<Aircraft> {
        return (await briefApi.put("/organizations/:orgId/aircraft/" + aircraft.id, aircraft)).data
    }

    export async function getAircraftWithId(aircraftId: string, orgId?: string): Promise<Aircraft> {
        return (await briefApi.get("/organizations/"+(orgId ?? ":orgId")+"/aircraft/"+aircraftId)).data
    }

    export async function getAllAircraftWbProfiles(): Promise<AircraftFullWbProfileInfo[]> {
        return (await briefApi.get("/aircraft/wbprofiles")).data
    }

    export async function getAircraftInfoByRegistration(registration: string): Promise<{icaoType: string}> {
        return (await briefApi.get("/aircraft/search/registration", {params:{registration}})).data
    }

    export async function getRoutes(): Promise<Route[]>{
        return (await briefApi.get("/organizations/:orgId/routes")).data
    }

    export async function getRouteWithId(routeId: string, orgId?: string): Promise<Route> {
        return (await briefApi.get("/organizations/"+(orgId ?? ":orgId")+"/routes/"+routeId)).data
    }

    export async function editRoute(route: Route): Promise<Route> {
        return (await briefApi.put("/organizations/:orgId/routes/"+route.id, {name: route.name, aftnAddresses: route.aftnAddresses, reverseRouteId: route.reverseRouteId || null })).data
    }

    export async function archiveRouteWithId(routeId: string): Promise<void> {
        return (await briefApi.delete("/organizations/:orgId/routes/"+routeId+"/archive")).data
    }
    
    export async function getImportedRoute(importedRouteId: string): Promise<ImportedRoute> {
        return await (await briefApi.get("/importedRoutes/"+importedRouteId)).data
    }

    export async function createImportedRouteFromMap(name: string, departure: RoutePoint, destination: RoutePoint, fixes: RoutePoint[]): Promise<{url: string}>{
        return (await briefApi.post("/importedRoutes/map", {name, departure, destination, fixes})).data
    }

    export async function createRoute(route: Route): Promise<Route> {
        return (await briefApi.post("/organizations/:orgId/routes", route)).data
    }

    export async function getFlights(): Promise<Flight[]> {
        return (await briefApi.get("/organizations/:orgId/flights")).data
    }

    export async function getFlightsInRange(from: Date, to: Date): Promise<Flight[]> {
        return (await briefApi.get("/organizations/:orgId/flights",{
            params: {
                from: from.toISOString(),
                to: to.toISOString()
            }
        })).data
    }

    export async function getFlightWithId(flightId: string, orgId?: string): Promise<Flight> {
        return (await briefApi.get("/organizations/"+(orgId ?? ":orgId")+"/flights/"+flightId)).data
    }
    
    //A flight is either ON a saved library route (`route`) or carries its own `customRoute` — never both,
    //and the API rejects the pair. The IFR planner mirrors a saved route's DEP/DEST into `customRoute`
    //so the (locked) aerodrome fields have somewhere to render; that mirror is display-only and must not
    //be persisted. Dropping it here keeps the invariant true for every create/edit path.
    function withoutDerivedCustomRoute(flight: Flight): Flight {
        if (!flight.route || !flight.customRoute) return flight
        const { customRoute, ...rest } = flight
        return rest as Flight
    }

    export async function editFlight(flight: Flight): Promise<Flight> {
        return (await briefApi.put("/organizations/:orgId/flights/"+flight.id, withoutDerivedCustomRoute(flight))).data
    }

    export async function editFlightWbLoads(flight: Flight, wb: FlightWb): Promise<Flight> {
        return (await briefApi.put("/organizations/:orgId/flights/"+flight.id+"/wbLoads", wb)).data
    }

    export async function createFlight(flight: Flight): Promise<Flight> {
        return (await briefApi.post("/organizations/:orgId/flights", withoutDerivedCustomRoute(flight))).data
    }

    export async function deleteFlight(flightId: string): Promise<{id: string}> {
        return (await briefApi.delete("/organizations/:orgId/flights/"+flightId)).data
    }

    export async function createReverseFlight(flightId: string, departureDateTime: Date): Promise<Flight> {
        return (await briefApi.post("/organizations/:orgId/flights/" + flightId + "/reverse", { departureDateTime: departureDateTime.toISOString() })).data
    }

    export async function duplicateFlight(flightId: string, departureDateTime: Date): Promise<Flight> {
        return (await briefApi.post("/organizations/:orgId/flights/" + flightId + "/duplicate", { departureDateTime: departureDateTime.toISOString() })).data
    }

    export async function getFlightAftnAddresses(flight: Flight): Promise<{aftnAddresses: string[]}> {
        return (await briefApi.post("/organizations/:orgId/flights/aftnAddresses",flight)).data
    }

    export async function getFlightBoardingPasses(flightId: string, orgId?: string): Promise<{pack: {url: string}, single:{url: string, passenger: Passenger}[]}> {
        return (await briefApi.get("/organizations/"+(orgId ?? ":orgId")+"/flights/"+flightId+"/boardingPasses")).data
    }

    export async function requestUpdateBoardingPasses(flightId: string): Promise<void> {
        return (await briefApi.post("/organizations/:orgId/flights/"+flightId+"/boardingPasses/sendPushUpdate")).data
    }

    export async function checkInBoardingPass(flightId: string, serialNumber: string): Promise<Passenger & {previousCheckIn: {time: Date, checkedInByUid: string }}> {
        return (await briefApi.post("/organizations/:orgId/flights/"+flightId+"/boardingPasses/" + serialNumber + "/checkIn")).data
    }

    export async function fileFpl(flightId: string): Promise<Flight> {
        return (await briefApi.post("/organizations/:orgId/flights/"+ flightId +"/fpl/file")).data
    }

    export async function refileFpl(flightId: string): Promise<FlightPlan> {
        return (await briefApi.post("/organizations/:orgId/flights/"+ flightId +"/fpl/refile")).data
    }
   
    export async function cancelFpl(flightId: string): Promise<Flight> {
        return (await briefApi.post("/organizations/:orgId/flights/"+ flightId +"/fpl/cancel")).data
    }

    export async function delayFpl(flightId: string, newTime: Date): Promise<Flight> {
        return (await briefApi.post("/organizations/:orgId/flights/"+ flightId +"/fpl/delay",{newTime: newTime.toISOString()})).data
    }

    export async function chgFpl(flightId: string, f15: string, f18: string): Promise<Flight> {
        return (await briefApi.post("/organizations/:orgId/flights/"+ flightId +"/fpl/chg",{f15, f18})).data
    }

    //Apply an IFPS-proposed route to a filed IFR flight: backend re-validates then sends a CHG (suspended)
    //or a fresh FPL (rejected). Returns the updated flight.
    export async function applyProposedRoute(flightId: string, route: string): Promise<Flight> {
        return (await briefApi.post("/organizations/:orgId/flights/"+ flightId +"/fpl/applyProposedRoute",{route})).data
    }

    export async function sendFplToEuroFpl(flightId: string): Promise<void> {
        return (await briefApi.post("/organizations/:orgId/flights/"+flightId+"/fpl/sendToEuroFpl")).data
    }

    export async function findIfrRoutes(flightId: string, optimizes?: string[], levels?: number[], overrides?: IfrPlanningOverrides): Promise<{ results: IfrRouteResult[] }> {
        return (await briefApi.post("/organizations/:orgId/flights/"+ flightId +"/fpl/route/find", { optimizes, levels, ...overrides })).data
    }

    //Stateless: plans from the given data — works for unsaved flights/plans and unsaved form edits.
    export async function findIfrRoutesFromData(input: IfrRouteFindInput): Promise<{ results: IfrRouteResult[] }> {
        return (await briefApi.post("/organizations/:orgId/ifr/routes/find", input)).data
    }

    //Stateless: checks a user-written Field-15 route verbatim (nav data + IFPS validation).
    export async function checkIfrRouteFromData(input: IfrRouteFindInput & { field15: string }): Promise<{ result: IfrRouteResult }> {
        return (await briefApi.post("/organizations/:orgId/ifr/routes/check", input)).data
    }

    export async function checkIfrRoute(flightId: string, field15: string, overrides?: IfrPlanningOverrides): Promise<{ result: IfrRouteResult }> {
        return (await briefApi.post("/organizations/:orgId/flights/"+ flightId +"/fpl/route/check", { field15, ...overrides })).data
    }

    export async function checkFlightPlanIfrRoute(flightPlanId: string, field15: string, overrides?: IfrPlanningOverrides): Promise<{ result: IfrRouteResult }> {
        return (await briefApi.post("/organizations/:orgId/flightplans/"+ flightPlanId +"/fpl/route/check", { field15, ...overrides })).data
    }

    export async function selectIfrRoute(flightId: string, field15: string, overrides?: IfrPlanningOverrides): Promise<{ flight: Flight }> {
        return (await briefApi.post("/organizations/:orgId/flights/"+ flightId +"/fpl/route/select", { field15, ...overrides })).data
    }

    export async function validateFpl(flightId: string): Promise<ValidateFplResponse> {
        return (await briefApi.post("/organizations/:orgId/flights/"+ flightId +"/fpl/validate")).data
    }

    export async function scheduleFileFpl(flightId: string, scheduleTime: Date): Promise<{id: Flight["id"], fplFile: Flight["fplFile"]}> {
        return (await briefApi.post("/organizations/:orgId/flights/"+flightId+"/fpl/schedule",{scheduleTime: scheduleTime.toISOString()})).data
    }

    export async function cancelScheduleFileFpl(flightId: string): Promise<{id: Flight["id"], fplFile: Flight["fplFile"]}> {
        return (await briefApi.delete("/organizations/:orgId/flights/"+flightId+"/fpl/schedule")).data
    }

    export async function requestBriefing(flightId: string): Promise<Flight> {
        return (await briefApi.post("/organizations/:orgId/flights/"+flightId+"/briefing/request")).data
    }

    export async function scheduleBriefingCreate(flightId: string, scheduleTime: Date): Promise<{id: Flight["id"], briefing: Flight["briefing"]}> {
        return (await briefApi.post("/organizations/:orgId/flights/"+flightId+"/briefing/schedule",{scheduleTime: scheduleTime.toISOString()})).data
    }

    export async function cancelScheduleBriefingCreate(flightId: string): Promise<{id: Flight["id"], briefing: Flight["briefing"]}> {
        return (await briefApi.delete("/organizations/:orgId/flights/"+flightId+"/briefing/schedule")).data
    }

    export async function discardBriefing(flightId: string): Promise<Flight> {
        return (await briefApi.delete("/organizations/:orgId/flights/"+flightId+"/briefing/discard")).data
    }

    export async function getBriefingStatus(flightId: string): Promise<{status: FlightBriefing["status"]}> {
        return (await briefApi.get("/organizations/:orgId/flights/"+flightId+"/briefing/status")).data
    }

    export async function getBriefingPdfUrls(flightId: string): Promise<{full: string}> {
        return (await briefApi.get("/organizations/:orgId/flights/"+flightId+"/briefing/pdfUrls")).data
    }

    export async function getPdfDoc(flightId: string, docName: string): Promise<any> {
        return (await briefApi.get("/organizations/:orgId/flights/"+flightId+"/docs/"+docName,{responseType:"blob"})).data
    }

    export async function getCustomPdfDoc(flightId: string, docName: string, getSignedDoc: boolean = false, allowEdit: boolean = false): Promise<any> {
        return (await briefApi.get("/organizations/:orgId/flights/"+flightId+"/docs/custom/" + docName,{responseType:"blob", params: {getSignedDoc, allowEdit}})).data
    }

    export async function createCustomPdfDoc(flightId: string, docName: string, blob: Blob): Promise<void> {
        return (await briefApi.post("/organizations/:orgId/flights/"+flightId+"/docs/custom/"+ docName, blob, {
            headers: {
                "Content-Type": "application/pdf"
            }
        })).data
    }

    export async function editCustomPdfDoc(flightId: string, docName: string, blob: Blob): Promise<void> {
        return (await briefApi.put("/organizations/:orgId/flights/"+flightId+"/docs/custom/"+ docName, blob, {
            headers: {
                "Content-Type": "application/pdf"
            }
        })).data
    }

    export async function deleteCustomPdfDoc(flightId: string, docName: string): Promise<void> {
        return (await briefApi.delete("/organizations/:orgId/flights/"+flightId+"/docs/custom/"+ docName)).data
    }

    export async function requestCustomPdfDocSignature(flightId: string, docName: string): Promise<void> {
        return (await briefApi.post("/organizations/:orgId/flights/"+flightId+"/docs/custom/"+ docName + "/requestSign")).data
    }

    export async function signCustomPdfDoc(flightId: string, docName: string, signatureBase64: string, acceptanceStatus: boolean): Promise<void> {
        return (await briefApi.post("/organizations/:orgId/flights/"+flightId+"/docs/custom/"+ docName + "/sign", {signatureBase64, acceptanceStatus})).data
    }

    //Direct printing on the org's on-site print server.
    export interface PrintableDocInfo { key: string, label: string, confirm: boolean, available: boolean, note?: string | null }

    export async function getPrintableDocs(flightId: string): Promise<{ printServerEnabled: boolean, docs: PrintableDocInfo[] }> {
        return (await briefApi.get("/organizations/:orgId/flights/"+flightId+"/docs/printableDocs")).data
    }

    export async function printFlightDoc(flightId: string, doc: string): Promise<{ status?: string, delivery?: string }> {
        return (await briefApi.post("/organizations/:orgId/flights/"+flightId+"/docs/print", { doc })).data
    }

    export async function sendRiskAssessmentEmailReminder(flightId: string): Promise<void> {
        return (await briefApi.post("/organizations/:orgId/flights/"+flightId+"/docs/riskAssessment/sendEmailReminder"))
    }

    export async function sendTrainingRecordEmailReminder(flightId: string): Promise<void> {
        return (await briefApi.post("/organizations/:orgId/flights/"+flightId+"/docs/trainingRecord/sendEmailReminder"))
    }

    export async function uploadWbPdf(flightId: string, blob: Blob): Promise<void> {
        await briefApi.post("/organizations/:orgId/flights/"+flightId+"/docs/uploadWbPdf", blob, {
            headers: {
                "Content-Type": "application/pdf"
            }
        })
    }

    //Last-minute W&B revision: the PDF goes up first (raw, like uploadWbPdf), then the revision itself
    //is committed — the backend appends the PDF to the briefing pack as an addendum.
    export async function uploadWbRevisionPdf(flightId: string, blob: Blob): Promise<void> {
        await briefApi.post("/organizations/:orgId/flights/"+flightId+"/docs/uploadWbRevisionPdf", blob, {
            headers: {
                "Content-Type": "application/pdf"
            }
        })
    }

    export async function submitWbRevision(flightId: string, revision: { changes: FlightWbRevisionChanges, wb: FlightWb, noShows: string[] }): Promise<Flight> {
        return (await briefApi.post("/organizations/:orgId/flights/"+flightId+"/briefing/wbRevision", revision)).data
    }

    export async function signWbRevision(flightId: string, n: number, signatureBase64: string): Promise<Flight> {
        return (await briefApi.post("/organizations/:orgId/flights/"+flightId+"/briefing/wbRevision/"+n+"/sign", { signatureBase64 })).data
    }

    export async function getWbRevisionPdf(flightId: string, n: number): Promise<any> {
        return (await briefApi.get("/organizations/:orgId/flights/"+flightId+"/docs/wb", { responseType: "blob", params: { rev: n } })).data
    }

    export async function pilotAcceptBriefing(flightId: string, signatureBase64: string, postFlightData: FlightPostFlightData): Promise<Flight> {
        return (await briefApi.post("/organizations/:orgId/flights/"+flightId+"/briefing/pilotAccept",{signatureBase64, postFlightData})).data
    }

    export async function getFlightWatchCookie(flightId: string): Promise<{cookie: string}> {
        return (await briefApi.get("/organizations/:orgId/flights/"+flightId+"/watch")).data
    }

    export async function getFlightPlans(): Promise<FlightPlan[]> {
        return (await briefApi.get("/organizations/:orgId/flightplans")).data
    }

    export async function getFlightPlansInRange(from: Date, to: Date): Promise<FlightPlan[]> {
        return (await briefApi.get("/organizations/:orgId/flightplans",{
            params: {
                from: from.toISOString(),
                to: to.toISOString()
            }
        })).data
    }

    export async function getFlightPlanWithId(flightPlanId: string, orgId?: string): Promise<FlightPlan> {
        return (await briefApi.get("/organizations/"+(orgId ?? ":orgId")+"/flightplans/"+flightPlanId)).data
    }

    export async function createFlightPlan(flightPlan: FlightPlan): Promise<FlightPlan> {
        return (await briefApi.post("/organizations/:orgId/flightplans", flightPlan)).data
    }
    
    export async function editFlightPlan(flightPlan: FlightPlan): Promise<FlightPlan> {
        return (await briefApi.put("/organizations/:orgId/flightplans/"+flightPlan.id, flightPlan)).data
    }

    export async function duplicateFlightPlan(flightPlanId: string, departureDateTime: Date): Promise<FlightPlan> {
        return (await briefApi.post("/organizations/:orgId/flightPlans/" + flightPlanId + "/duplicate", { departureDateTime: departureDateTime.toISOString() })).data
    }

    export async function reverseFlightPlan(flightPlanId: string, departureDateTime: Date): Promise<FlightPlan> {
        return (await briefApi.post("/organizations/:orgId/flightPlans/" + flightPlanId + "/reverse", { departureDateTime: departureDateTime.toISOString() })).data
    }

    export async function deleteFlightPlan(flightPlanId: string): Promise<{id: string}> {
        return (await briefApi.delete("/organizations/:orgId/flightplans/"+flightPlanId)).data
    }

    export async function getFlightPlanPdfDoc(flightPlanId: string, docName: "icaoFpl" | "genDec" | "form731"): Promise<any> {
        return (await briefApi.get("/organizations/:orgId/flightplans/"+flightPlanId+"/docs/"+docName,{responseType:"blob"})).data
    }

    export async function fileFlightPlanFpl(flightPlanId: string): Promise<FlightPlan> {
        return (await briefApi.post("/organizations/:orgId/flightplans/"+ flightPlanId +"/fpl/file")).data
    }

    export async function refileFlightPlanFpl(flightPlanId: string): Promise<FlightPlan> {
        return (await briefApi.post("/organizations/:orgId/flightplans/"+ flightPlanId +"/fpl/refile")).data
    }

    export async function delayFlightPlanFpl(flightPlanId: string, newTime: Date): Promise<FlightPlan> {
        return (await briefApi.post("/organizations/:orgId/flightplans/"+ flightPlanId +"/fpl/delay",{newTime: newTime.toISOString()})).data
    }

    export async function chgFlightPlanFpl(flightPlanId: string, f15: string, f18: string): Promise<Flight> {
        return (await briefApi.post("/organizations/:orgId/flightplans/"+ flightPlanId +"/fpl/chg",{f15, f18})).data
    }

    //Apply an IFPS-proposed route to a filed IFR flight plan (CHG if suspended, fresh FPL if rejected).
    export async function applyFlightPlanProposedRoute(flightPlanId: string, route: string): Promise<FlightPlan> {
        return (await briefApi.post("/organizations/:orgId/flightplans/"+ flightPlanId +"/fpl/applyProposedRoute",{route})).data
    }

    export async function cancelFlightPlanFpl(flightPlanId: string): Promise<FlightPlan> {
        return (await briefApi.post("/organizations/:orgId/flightplans/"+ flightPlanId +"/fpl/cancel")).data
    }

    export async function findFlightPlanIfrRoutes(flightPlanId: string, optimizes?: string[], levels?: number[], overrides?: IfrPlanningOverrides): Promise<{ results: IfrRouteResult[] }> {
        return (await briefApi.post("/organizations/:orgId/flightplans/"+ flightPlanId +"/fpl/route/find", { optimizes, levels, ...overrides })).data
    }

    export async function selectFlightPlanIfrRoute(flightPlanId: string, field15: string, overrides?: IfrPlanningOverrides): Promise<{ flightPlan: FlightPlan }> {
        return (await briefApi.post("/organizations/:orgId/flightplans/"+ flightPlanId +"/fpl/route/select", { field15, ...overrides })).data
    }

    export async function validateFlightPlanFpl(flightPlanId: string): Promise<ValidateFplResponse> {
        return (await briefApi.post("/organizations/:orgId/flightplans/"+ flightPlanId +"/fpl/validate")).data
    }

    export async function getFlightPlanAftnAddresses(flightPlan: FlightPlan): Promise<{aftnAddresses: string[]}> {
        return (await briefApi.post("/organizations/:orgId/flightplans/aftnAddresses",flightPlan)).data
    }

    export async function getAirfields(): Promise<Airfield[]> {
        return (await briefApi.get("/airfield")).data
    }

    export async function getAirfieldSlotTimesInfoForDate(airfieldId: string, dateStr: string): Promise<{slotTimes: AirfieldSlotTime[], airfield: Airfield}> {
        const info: {slotTimes: AirfieldSlotTime[], airfield: Airfield} = (await briefApi.get("/airfield/" + airfieldId + "/slotTimes/" + dateStr)).data
        info.slotTimes.forEach((st) => {
            st.startDateTime = moment(st.startDateTimeEpoch * 1000).utc(false).toDate();
        })

        return info
    }

    export async function bookAirfieldSlot(airfieldId: string, dateStr: string, pendingSlotId: string, callsign: string, remarks: string, contactDetails: AirfieldSlotContactDetails): Promise<void>{
        return await briefApi.post("/airfield/" + airfieldId + "/slotTimes/" + dateStr, {pendingSlotId, callsign, remarks, contactDetails})
    }

    export async function cancelBookedAirfieldSlot(airfieldId: string, dateStr: string, bookedSlotId: string): Promise<void>{
        return await briefApi.delete("/airfield/" + airfieldId + "/slotTimes/" + dateStr + "/" + bookedSlotId)    
    }

    export async function setTimeInAirfieldBookedSlot(airfieldId: string, dateStr: string, bookedSlotId: string, type: "blocksOff" | "takeoff" | "landed"): Promise<void>{
        return await briefApi.put("/airfield/" + airfieldId + "/slotTimes/" + dateStr + "/" + bookedSlotId + "/settime/" + type)
    }

    export async function addAirfieldSlotRestriction(airfieldId: string, dateStr: string, startDateTime: Date, endDateTime: Date, slotType: AirfieldSlotType, reason: string): Promise<void>{
        return await briefApi.post("/airfield/" + airfieldId + "/slotTimes/" + dateStr + "/restrictions", {
            startDateTimeEpoch: startDateTime.getTime() /1000,
            endDateTimeEpoch: endDateTime.getTime() /1000,
            slotType,
            reason
        })
    }

    export async function editAirfieldSlotRestriction(airfieldId: string, dateStr: string, restrictionId: string, startDateTime: Date, endDateTime: Date, slotType: AirfieldSlotType, reason: string): Promise<void>{
        return await briefApi.put("/airfield/" + airfieldId + "/slotTimes/" + dateStr + "/restrictions/" + restrictionId, {
            startDateTimeEpoch: startDateTime.getTime() /1000,
            endDateTimeEpoch: endDateTime.getTime() /1000,
            slotType,
            reason
        })
    }

    export async function acceptRequestedAirfieldSlot(airfieldId: string, dateStr: string, requestedSlotId: string): Promise<void>{
        return await briefApi.post("/airfield/" + airfieldId + "/slotTimes/" + dateStr + "/" + requestedSlotId + "/accept")
    }

    export async function rejectRequestedAirfieldSlot(airfieldId: string, dateStr: string, requestedSlotId: string): Promise<void>{
        return await briefApi.post("/airfield/" + airfieldId + "/slotTimes/" + dateStr + "/" + requestedSlotId + "/reject")
    }

    export async function shortLinkExpandToFullUrl(token: string): Promise<{url: string}>{
        return (await briefApi.get("/shortlinks/" + token)).data
    }

    export async function autorouterExchangeCode(code: string): Promise<void> {
        return (await briefApi.post("/organizations/:orgId/autorouter/exchangeCode", { code: code })).data
    }

    export async function autorouterGetLinkStatus(): Promise<AutorouterStatus> {
        return (await briefApi.get("/organizations/:orgId/autorouter/status")).data
    }

    export async function createIndividualOrganization(data: {firstName: string, lastName: string, flightPlanPicDetails: string, weight: number}): Promise<void> {
        return (await briefApi.post("/organizations/create/individual", data))
    }

    export async function requestOrganizationCreate(data: any): Promise<void> {
        return (await briefApi.post("/organizations/requestCreate", data))
    }

    export async function requestCreditsPurchaseLink(product: "45_brief_credits"|"285_brief_credits"): Promise<{url: string}>{
        return (await briefApi.get("/organizations/:orgId/credits/purchaseLink/" + product)).data
    }

    export async function getToolsPdfDoc(toolsFlight: ToolsFlight, docName: "form731"|"gendec"|"icaofpl"): Promise<any> {
        return (await briefApi.post("/tools/flightdocs/"+docName, toolsFlight, {responseType: "blob"})).data
    }

    export async function getToolsGramet(toolsFlight: ToolsFlight): Promise<Blob> {
        return (await briefApi.post("/tools/flightdocs/gramet", toolsFlight, {responseType: "blob"})).data
    }
}
 
